import * as CL from '@design-system/component-library';
import React from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { useDebounce } from '../../common/hooks/useDebounce';
import {
  billingAccountNameMsg,
  numberMsg,
  payerMsg,
  pcsMsg,
  referenceMsg,
  searchPlaceHolderMsg,
  t,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { getDeliveryMethod } from '../../common/utils/billingAccountUtils.js';
import {
  HighlightSearchResult,
  ScheduledChangeTimestamp,
  highlightKeyword,
} from '../../common/utils/searchFieldUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { Grid } from '../Grid/Grid.js';
import { CloseButton, InputSearch } from '../InputSearch/InputSearch';
import { Table, TableDescription } from '../Table/index.js';
import type { CLTableColumn } from '../../common/types/componentLibrary';
import type { BillingAccountsResponse } from '../../generated/api/models.js';

import './SelectableBillingAccountList.scss';

export interface SelectableBillingAccountListProps {
  billingAccounts?: BillingAccountsResponse;
  defaultBillingAccountId?: string;
  defaultSelectedBillingAccountIds?: string[];
  handleBillingAccountIdsChange?: (selectedBillingAccountIds: string[]) => void;
}

export const SelectableBillingAccountList = ({
  billingAccounts,
  defaultBillingAccountId,
  defaultSelectedBillingAccountIds,
  handleBillingAccountIdsChange,
}: SelectableBillingAccountListProps) => {
  const { authenticatedUser } = useAuth();
  const [selected, setSelected] = React.useState<string[]>(defaultSelectedBillingAccountIds || []);
  const [searchKeyword, setSearchKeyword] = React.useState<string>('');

  const debounceSearch = useDebounce((value: string) => setSearchKeyword(value), 300);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const getSelectedBillingAccountIdsCount = (ids: string[]) => {
    const selectedCount = ids.filter(id => id !== defaultBillingAccountId).length;
    return defaultBillingAccountId ? selectedCount + 1 : selectedCount;
  };

  if (defaultBillingAccountId && !selected.includes(defaultBillingAccountId)) {
    selected.push(defaultBillingAccountId);
  }

  const columns: CLTableColumn[] = [
    {
      key: 'selectCheckbox',
      label: (
        <CL.Checkbox
          id="select-all-billing-accounts"
          onChange={e => {
            const allBillingAccountIds =
              billingAccounts?.searchResults
                ?.map(ba => ba.result.billingAccountId)
                .filter((id): id is string => id !== undefined) || [];
            if (handleBillingAccountIdsChange) {
              handleBillingAccountIdsChange(e.currentTarget.checked ? allBillingAccountIds : []);
              setSelected(e.currentTarget.checked ? allBillingAccountIds : []);
            }
          }}
        />
      ),
      sortable: false,
      width: '2%',
    },
    { key: 'billingAccountDisplayId', label: t.CCB1(numberMsg), sortable: false, width: '20%' },
    { key: 'billingAccountName', label: t.RH6T(billingAccountNameMsg), sortable: false, width: '39%' },
    { key: 'payerName', label: t.PB6S(payerMsg), sortable: false, width: '39%' },
  ];

  const doesFieldMatchSearch = (field: string | undefined, s: string) => {
    return field?.toLowerCase().includes(s.toLowerCase());
  };

  const rows = billingAccounts?.searchResults
    ?.filter(row => {
      const ba = row.result;
      return searchKeyword
        ? [
            'billingAccountDisplayId',
            'billingAccountName',
            'payerName',
            'payerNameExtension',
            'billingContactName',
            'billingAccountExtensionName',
            'billingContactPhone',
            'billingContactEmail',
          ].some(field => doesFieldMatchSearch(String(ba[field as keyof typeof ba] ?? ''), searchKeyword))
        : true;
    })
    .map(row => {
      const ba = row.result;

      const BillingAccountDisplayId = () => (
        <TableDescription
          description={
            ba.deliveryMethod ? (
              <>
                {getDeliveryMethod(ba.deliveryMethod)}
                <br />
                {row.matchedFields &&
                  searchKeyword &&
                  HighlightSearchResult(
                    row.matchedFields,
                    ba,
                    [
                      { columnId: 'customerReference1', translation: t.LVRN(referenceMsg) + ' 1' },
                      { columnId: 'customerReference2', translation: t.LVRN(referenceMsg) + ' 2' },
                      { columnId: 'customerReference3', translation: t.LVRN(referenceMsg) + ' 3' },
                      { columnId: 'customerReference4', translation: t.LVRN(referenceMsg) + ' 4' },
                    ],
                    searchKeyword
                  )}
              </>
            ) : undefined
          }
          title={<div>{highlightKeyword(ba.billingAccountDisplayId ?? '-', searchKeyword)}</div>}
        />
      );

      const isChecked =
        (ba.billingAccountId !== undefined && selected?.includes(ba.billingAccountId)) ||
        defaultBillingAccountId === ba.billingAccountId;

      const getNewSelectedItems = () => {
        return ba.billingAccountId !== undefined && selected.includes(ba.billingAccountId)
          ? selected.filter(id => id !== ba.billingAccountId)
          : [...selected, ba.billingAccountId].filter(id => id !== undefined);
      };

      const handleOnChange = () => {
        const newSelectedItems = getNewSelectedItems();
        if (handleBillingAccountIdsChange) {
          handleBillingAccountIdsChange(newSelectedItems);
        }
        setSelected(newSelectedItems);
      };

      return {
        selectCheckbox: (
          <CL.Checkbox
            key={String(isChecked)}
            value={String(ba.billingAccountId)}
            disabled={defaultBillingAccountId === ba.billingAccountId}
            checked={isChecked}
            onChange={handleOnChange}
          />
        ),
        billingAccountDisplayId: <BillingAccountDisplayId />,
        billingAccountName: (
          <TableDescription
            description={
              ba.billingAccountExtensionName && highlightKeyword(ba.billingAccountExtensionName, searchKeyword)
            }
            title={ba.billingAccountName && highlightKeyword(ba.billingAccountName, searchKeyword)}
          />
        ),
        payerName: (
          <TableDescription
            description={ba.payerNameExtension && highlightKeyword(ba.payerNameExtension, searchKeyword)}
            title={ba.payerName ? highlightKeyword(ba.payerName, searchKeyword) : ba.payerName}
          />
        ),
        billingContactName: (
          <TableDescription
            title={ba.billingContactName && highlightKeyword(ba.billingContactName, searchKeyword)}
            description={
              ba.billingContactPhone
                ? `${ba.billingContactPhone}, ${ba.billingContactEmail}`
                : `${ba.billingContactEmail ?? '-'}`
            }
          />
        ),
        subscriptionCount: <>{ba.subscriptionCount || 0}</>,
        companyName: <>{getCompanyName(authenticatedUser, row.result.accountMasterId)}</>,
        scheduledChangeTimestamp: (
          <ScheduledChangeTimestamp
            scheduledChangeTimestamp={ba.scheduledChangeTimestamp}
            style="of-selectable-billing-account-list__scheduled-timestamp"
          />
        ),
      };
    });

  return (
    <Grid>
      <>
        <div className={dsClass.MARGIN_BOTTOM_4}>
          <b>
            {t.IVYK('Selected')}: {getSelectedBillingAccountIdsCount(selected)} {t.B3MG(pcsMsg)}
          </b>
        </div>
        <div className="of-list-search">
          <div className={dsClass.INPUTGROUP_INPUTCONTAINER}>
            <div className={`${dsClass.INPUT} ${dsClass.INPUT_WITHICON} of-list-search__input`}>
              <InputSearch
                name="tableSearch"
                type="search"
                placeholder={t.GGLB(searchPlaceHolderMsg)}
                onChange={e => debounceSearch(e.target.value)}
                ref={r => {
                  r && !searchKeyword && (r.value = '');
                }}
              >
                {!!searchKeyword && (
                  <CloseButton
                    onClick={() => {
                      setSearchKeyword('');
                      if (inputRef.current) {
                        inputRef.current.value = '';
                      }
                    }}
                  />
                )}
              </InputSearch>
            </div>
          </div>
        </div>
        <div className="of-selectable-billing-account__scrollable-table">
          <Table columns={columns} noItemsText={t.NVGB('No billing accounts')} rows={rows || []} />
        </div>
      </>
    </Grid>
  );
};
